// @ts-nocheck
import {
    getStoreStatus,
    calculateStoreStatus,
    isStoreOpen,
    getStoreTimezone,
    fmtTime as sharedFmtTime,
    getRawHoursArray
} from './store-status.js';

/**
 * Store Locator - Main class for managing store list and detail views
 * Handles Google Maps integration, store search, filtering, and detail view
 */

// Type declarations for window properties to avoid TypeScript errors
/**
 * @typedef {Object} GoogleMapSettings
 * @property {string} apiKey
 * @property {string} pinIcon
 * @property {string} defaultLat
 * @property {string} defaultLng
 * @property {number} defaultZoom
 * @property {number} radiusKm
 * @property {number} maxResults
 * @property {boolean} enableBrandPins
 * @property {string} defaultPinImage
 * @property {Array<{name: string, pinImage: string}>} brandConfigs
 */

// Helper to access window properties with type safety
/**
 * @returns {any}
 */
function getWindow() {
    return /** @type {any} */ (window);
}

// Debounce function
function debounce(func, delay) {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
}

class StoreLocator {
    constructor() {
        // All DOM selectors - single source of truth
        this.selectors = {
            // Map elements
            map: {
                listMap: '[id^="google-map-"]',
                detailMap: '#store-detail-map'
            },
            // List view elements
            list: {
                container: '#store-locator-list',
                storeList: '.store-list',
                countHeader: '.store-count-header',
                storeCard: '.store-card',
                storeDetailsLink: '.store-details-link'
            },
            // Detail view elements
            detail: {
                container: '#store-locator-detail',
                loading: '#store-detail-loading',
                content: '#store-detail-content',
                error: '#store-detail-error',
                name: '#store-detail-name',
                address: '#store-detail-address',
                phone: '#store-detail-phone',
                email: '#store-detail-email',
                contactBtn: '#store-detail-contact-btn',
                hours: '#store-detail-hours',
                status: '#store-detail-status',
                statusText: '#store-detail-status-text',
                statusDot: '.store-locator__status-dot',
                about: '#store-detail-about',
                breadcrumbStore: '#store-detail-breadcrumb-store',
                manager: '#store-detail-manager'
            },
            // Search and filter elements
            search: {
                input: '.main-input',
                searchBtn: '.primary-search-btn',
                expandBtn: '.expand-btn',
                useMyLocationBtn: '.use-my-location',
                filterCheckboxes: '.filter-section input[type="checkbox"]',
                filterSection: '.filter-section'
            },
            // Data attributes
            data: {
                pageUrl: '[data-store-locator-page-url]',
                hideOnDetail: '[data-store-locator-hide-on-detail]'
            }
        };

        // State variables
        this.map = null;
        this.detailMap = null;
        /** @type {any[]} */
        this.storeMarkers = [];
        this.markerCluster = null;
        this.radiusKm = 200;
        this.maxResults = 20;
        /** @type {any[]} */
        this.brandConfigs = [];
        /** @type {any[]} */
        this.typeConfigs = [];
        /** @type {string|null} */
        this.defaultPinImage = null;
        this.enableBrandPins = false;
        this.isGoogleMapsAvailable = false;
        this.googleMapsCheckTimeout = null;
    }

    /**
     * Initialize the store locator
     */
    async init() {
        // Check for Google Maps availability with timeout
        this.checkGoogleMapsAvailability();

        const metaobjectId = document.getElementById('store-locator-detail')?.getAttribute('data-metaobject-id');
        if (metaobjectId) {
            this.initDetailView(metaobjectId);
        } else {
            this.initListView();
        }

        document.querySelector('main[data-template="page.store-locator"]')?.style.setProperty('opacity', '1');
        this.initExpandButton();
    }

    /**
     * Check if Google Maps API is available
     * Sets up timeout to detect if Maps fails to load
     */
    checkGoogleMapsAvailability = debounce(function () {
        const win = getWindow();

        // Check immediately
        if (typeof win.google !== 'undefined' && win.google?.maps) {
            this.isGoogleMapsAvailable = true;
            return;
        }

        // Set up timeout to detect failure
        if (typeof win.google === 'undefined' || !win.google?.maps) {
            this.isGoogleMapsAvailable = false;
            this.handleGoogleMapsUnavailable();
        }

        // Also check periodically
        if (typeof win.google !== 'undefined' && win.google?.maps) {
            this.isGoogleMapsAvailable = true;
        }
    }, 1000);

    /**
     * Handle when Google Maps is unavailable
     */
    handleGoogleMapsUnavailable() {
        console.warn('Google Maps is unavailable, using fallback mode');
        this.isGoogleMapsAvailable = false;

        // Show fallback message (replaces map in same position)
        this.showMapUnavailableMessage();

        // Disable location-based features
        const useMyLocationBtn = document.querySelector(this.selectors.search.useMyLocationBtn);
        const expandBtn = document.querySelector(this.selectors.search.expandBtn);
        if (useMyLocationBtn && useMyLocationBtn instanceof HTMLElement) {
            useMyLocationBtn.style.display = 'none';
        }
        if (expandBtn && expandBtn instanceof HTMLElement) {
            expandBtn.style.display = 'none';
        }

        // Load stores without location filtering
        this.fetchStoreList().then((response) => {
            if (response?.data) {
                getWindow().STORE_LOCATIONS = response.data;
                // Sort alphabetically
                const sortedStores = this.sortStoresAlphabetically(response.data);
                this.renderStoreResults(sortedStores);
                this.initTextBasedSearch();
            }
        }).catch((err) => {
            console.error('Error fetching stores:', err);
        });

        // Initialize filters
        this.setupFilters();
    }

    /**
     * Show message indicating map is temporarily unavailable
     */
    showMapUnavailableMessage() {
        // Check if message already exists
        const existingMessage = document.querySelector('.map-unavailable-message');
        if (existingMessage) {
            return;
        }

        const mapWrapper = document.querySelector('.map-wrapper');
        if (!mapWrapper) {
            console.warn('Map wrapper not found, cannot display message');
            return;
        }

        // Hide the map canvas inside map-wrapper
        const mapCanvas = mapWrapper.querySelector('.map-canvas');
        const mapPreviewContainer = mapWrapper.querySelector('.map-preview-container');
        if (mapCanvas && mapCanvas instanceof HTMLElement) {
            mapCanvas.style.display = 'none';
        }
        if (mapPreviewContainer && mapPreviewContainer instanceof HTMLElement) {
            mapPreviewContainer.style.display = 'none';
        }

        // Create message element
        const messageEl = document.createElement('div');
        messageEl.className = 'map-unavailable-message';
        messageEl.setAttribute('role', 'alert');
        messageEl.setAttribute('aria-live', 'polite');
        messageEl.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 657px;
            min-height: 400px;
            padding: 40px 24px;
            background-color: #fff3cd;
            border: 2px solid #ffc107;
            border-radius: 8px;
            text-align: center;
            color: #856404;
            font-size: 15px;
            line-height: 1.6;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            box-sizing: border-box;
        `;
        messageEl.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 16px;">
                <span style="font-size: 32px;">⚠️</span>
                <strong style="font-size: 20px; font-weight: 600;">Map Temporarily Unavailable</strong>
            </div>
            <p style="margin: 0; font-size: 16px; max-width: 500px;">The map service is currently unavailable. Please use the search and filters up to find stores.</p>
        `;

        // Insert message inside map-wrapper (same position as map)
        mapWrapper.appendChild(messageEl);
    }

    /**
     * Sort stores alphabetically by name
     */
    /**
     * @param {any[]} stores
     */
    sortStoresAlphabetically(stores) {
        return [...stores].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }

    /**
     * Initialize list view (map, search, filters)
     */
    initListView() {
        // Map will be initialized by Google Maps callback (window.initMap)
        this.setupFilters();
    }

    /**
     * Setup filter checkboxes
     */
    setupFilters() {
        const filterSection = document.querySelector(this.selectors.search.filterSection);
        if (!filterSection) return;
        filterSection.addEventListener('change', (e) => {
            const target = e.target;
            if (target && target instanceof HTMLInputElement && target.type === 'checkbox') {
                this.renderStores();
                this.renderStoreResults((/** @type {any} */ (getWindow().STORE_LOCATIONS)) || []);
            }
        });
    }

    /**
     * Initialize Google Maps for list view
     * Called by Google Maps API callback
     */
    initMap() {

        const el = document.querySelector(this.selectors.map.listMap);
        if (!el) return;

        const win = getWindow();
        // Check if Google Maps is actually available
        if (typeof win.google === 'undefined' || !win.google?.maps) {
            this.handleGoogleMapsUnavailable();
            return;
        }

        // Clear timeout since Maps loaded successfully
        if (this.googleMapsCheckTimeout) {
            clearTimeout(this.googleMapsCheckTimeout);
        }
        this.isGoogleMapsAvailable = true;

        const settings = win.GOOGLE_MAP_SETTINGS || {};
        const lat = Number(settings.defaultLat) || -25.2744;
        const lng = Number(settings.defaultLng) || 133.7751;
        const zoom = Number(settings.defaultZoom) || 5;

        // Ensure map-related settings (including pin configs) are loaded for both list and detail views
        this.loadMapSettings(settings);

        try {
            const google = win.google;
            this.map = new google.maps.Map(el, {
                center: { lat, lng },
                zoom,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
            });

            this.map.addListener('click', () => {
                this.closeAllInfoWindows();
            });

            this.fetchStoreList().then((response) => {
                getWindow().STORE_LOCATIONS = response?.data;
                this.renderStores();
                this.renderStoreResults(response?.data);
                this.initSearch();
                this.initUseMyLocation();
            }).catch((err) => {
                console.error('Error fetching stores:', err);
            });
        } catch (error) {
            console.error('Error initializing Google Maps:', error);
            this.handleGoogleMapsUnavailable();
        }
    }

    /**
     * Initialize search functionality
     */
    initSearch() {
        // If Google Maps is not available, use text-based search
        if (!this.isGoogleMapsAvailable) {
            this.initTextBasedSearch();
            return;
        }

        const input = document.querySelector(this.selectors.search.input);
        const searchBtn = document.querySelector(this.selectors.search.searchBtn);
        if (!input) return;

        const google = getWindow().google;
        if (!google || !google.maps || !google.maps.places) {
            this.initTextBasedSearch();
            return;
        }

        try {
            const autocomplete = new google.maps.places.Autocomplete(input, {
                types: ['(regions)'],
                componentRestrictions: { country: 'au' }
            });

            this.setupAutocompleteStyles();

            const getPacItemText = (pacItem) => {
                if (!pacItem) return '';
                
                const queryEl = pacItem.querySelector('.pac-item-query');
                let queryText = queryEl ? (queryEl.textContent || '').trim() : '';

                const secondarySpans = Array.from(pacItem.querySelectorAll('span:not(.pac-icon):not(.pac-item-query)'));
                let secondaryText = secondarySpans.map((s) => (s.textContent || '').trim()).filter(Boolean).join(' ');

                let raw = '';
                if (queryText && secondaryText) {
                    if (secondaryText.toLowerCase().includes(queryText.toLowerCase())) {
                        raw = secondaryText;
                    } else if (queryText.toLowerCase().includes(secondaryText.toLowerCase())) {
                        raw = queryText;
                    } else {
                        // Check if queryText ends with a comma or space
                        const separator = queryText.match(/[\s,]$/) ? '' : ' ';
                        raw = `${queryText}${separator}${secondaryText}`;
                    }
                } else {
                    raw = queryText || secondaryText || (pacItem.innerText || pacItem.textContent || '');
                }
                
                return raw.replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim();
            };

            let geocoderInstance = null;

            const hidePacContainers = () => {
                const pacContainers = document.querySelectorAll('.pac-container');
                pacContainers.forEach((container) => {
                    if (container instanceof HTMLElement) {
                        container.style.display = 'none';
                    }
                });
            };

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place && place.geometry && place.geometry.location) {
                    const location = place.geometry.location;
                    this.setUserLocation(location.lat(), location.lng());
                }
            });

            const performAutocompleteSearch = async () => {
                const value = (input instanceof HTMLInputElement ? input.value : '').trim();
                if (!value) {
                    this.fetchStoreList().then((response) => {
                        getWindow().STORE_LOCATIONS = response?.data;
                        this.renderStores();
                        this.renderStoreResults(response?.data);
                    });
                    return;
                }

                // 1. Check if autocomplete already has a place selected with geometry
                let place = autocomplete.getPlace();
                if (place && place.geometry && place.geometry.location) {
                    const location = place.geometry.location;
                    this.setUserLocation(location.lat(), location.lng());
                    return;
                }

                // 2. If pac-container suggestions are visible, select first suggestion and search
                const pacContainer = document.querySelector('.pac-container');
                if (pacContainer && pacContainer.style.display !== 'none') {
                    const firstPacItem = pacContainer.querySelector('.pac-item');
                    if (firstPacItem) {
                        const addressText = getPacItemText(firstPacItem);
                        if (addressText) {
                            if (input instanceof HTMLInputElement) {
                                input.value = addressText;
                            }
                            hidePacContainers();
                            this.geocodeAndSearch(addressText);
                            return;
                        }
                    }
                }

                // 3. Fallback: Geocode the entered postcode or suburb string directly using google.maps.Geocoder
                this.geocodeAndSearch(value);
            };

            // Automatically fill clean address from first autocomplete suggestion when clicking outside (without searching)
            const handleOutsideClick = (e) => {
                const pacContainers = Array.from(document.querySelectorAll('.pac-container'));
                const activePacContainer = pacContainers.find(
                    (container) => container instanceof HTMLElement && container.style.display !== 'none'
                );

                // Exit immediately if autocomplete dropdown is NOT open to avoid interfering with map or marker clicks
                if (!activePacContainer) {
                    return;
                }

                const target = /** @type {HTMLElement} */ (e.target);
                const searchWrapper = document.querySelector('.search-input-wrapper') || input.parentElement;

                const isOutsideInput = input && !input.contains(target);
                const isOutsideWrapper = !searchWrapper || !searchWrapper.contains(target);
                const isOutsideSearchBtn = !searchBtn || !searchBtn.contains(target);
                const isOutsidePac = pacContainers.every((container) => !container.contains(target));

                if (isOutsideInput && isOutsideWrapper && isOutsideSearchBtn && isOutsidePac) {
                    const firstPacItem = activePacContainer.querySelector('.pac-item');
                    if (firstPacItem) {
                        const addressText = getPacItemText(firstPacItem);
                        if (addressText && input instanceof HTMLInputElement) {
                            const google = getWindow().google;
                            if (google && google.maps && google.maps.Geocoder) {
                                if (!geocoderInstance) {
                                    geocoderInstance = new google.maps.Geocoder();
                                }
                                geocoderInstance.geocode(
                                    { address: addressText, componentRestrictions: { country: 'au' } },
                                    (results, status) => {
                                        if (status === 'OK' && results && results[0] && results[0].formatted_address) {
                                            const formatted = results[0].formatted_address;
                                            if (input instanceof HTMLInputElement) {
                                                input.value = formatted;
                                            }
                                        }
                                    }
                                );
                            }
                        }
                    }

                    hidePacContainers();
                    if (input instanceof HTMLElement) {
                        input.blur();
                    }
                }
            };

            document.addEventListener('pointerdown', handleOutsideClick, true);
            document.addEventListener('mousedown', handleOutsideClick, true);

            input.addEventListener('blur', () => {
                hidePacContainers();
            });

            if (searchBtn) {
                searchBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    performAutocompleteSearch();
                });
            }

            input.addEventListener('keydown', (e) => {
                if (e instanceof KeyboardEvent && e.key === 'Enter') {
                    // Check if a Google Autocomplete suggestion is highlighted
                    const hasHighlightedSuggestion = document.querySelector('.pac-container .pac-item-selected');
                    if (hasHighlightedSuggestion) {
                        // Let Google Maps Autocomplete handle the selection, which triggers 'place_changed'
                        return;
                    }
                    e.preventDefault();
                    performAutocompleteSearch();
                }
            });
        } catch (error) {
            console.error('Error initializing Google Places Autocomplete:', error);
            this.initTextBasedSearch();
        }
    }

    /**
     * Geocode a location string (suburb or postcode) and search stores
     * @param {string} locationString
     */
    geocodeAndSearch(locationString) {
        if (!locationString) return;

        const google = getWindow().google;
        if (!google || !google.maps || !google.maps.Geocoder) {
            this.fetchStoreList().then((response) => {
                getWindow().STORE_LOCATIONS = response?.data;
                this.renderStores();
                this.renderStoreResults(response?.data);
            });
            return;
        }

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                address: locationString,
                componentRestrictions: { country: 'au' }
            },
            (results, status) => {
                if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
                    const loc = results[0].geometry.location;
                    const input = document.querySelector(this.selectors.search.input);
                    if (results[0].formatted_address && input instanceof HTMLInputElement) {
                        input.value = results[0].formatted_address;
                    }
                    this.setUserLocation(loc.lat(), loc.lng());
                } else {
                    console.warn('Geocoding failed for:', locationString, status);
                    this.fetchStoreList().then((response) => {
                        getWindow().STORE_LOCATIONS = response?.data;
                        this.renderStores();
                        this.renderStoreResults(response?.data);
                    });
                }
            }
        );
    }

    /**
     * Initialize text-based search (fallback when Google Maps is unavailable)
     */
    initTextBasedSearch() {
        const input = document.querySelector(this.selectors.search.input);
        const searchBtn = document.querySelector(this.selectors.search.searchBtn);
        if (!input) return;

        // Update placeholder to indicate text search
        if (input instanceof HTMLInputElement) {
            const originalPlaceholder = input.placeholder || 'Search stores...';
            input.placeholder = originalPlaceholder.replace(/location|address/gi, 'store name or address');
        }

        const performTextSearch = () => {
            const searchTerm = (input instanceof HTMLInputElement ? input.value : '').trim().toLowerCase();
            const stores = (getWindow().STORE_LOCATIONS) || [];

            if (!searchTerm) {
                // Show all stores sorted alphabetically
                const sortedStores = this.sortStoresAlphabetically(stores);
                this.renderStoreResults(sortedStores);
                return;
            }

            // Filter stores by name or address
            const filteredStores = stores.filter((/** @type {any} */ store) => {
                const name = (store.name || '').toLowerCase();
                const addressLower = this.formatStoreAddress(store.address).toLowerCase();

                return name.includes(searchTerm) || addressLower.includes(searchTerm);
            });

            // Sort filtered results alphabetically
            const sortedStores = this.sortStoresAlphabetically(filteredStores);
            this.renderStoreResults(sortedStores);
        };

        // Search on button click
        if (searchBtn) {
            searchBtn.addEventListener('click', (e) => {
                e.preventDefault();
                performTextSearch();
            });
        }

        // Search on Enter key
        input.addEventListener('keypress', (e) => {
            if (e instanceof KeyboardEvent && e.key === 'Enter') {
                e.preventDefault();
                performTextSearch();
            }
        });

        // Optional: Search as user types (debounced)
        /** @type {number | null} */
        let searchTimeout = null;
        input.addEventListener('input', () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            searchTimeout = setTimeout(() => {
                performTextSearch();
            }, 300);
        });
    }

    /**
     * Setup autocomplete dropdown styles
     */
    setupAutocompleteStyles() {
        const searchWrapper = document.querySelector('.search-input-wrapper');
        if (!searchWrapper) return;

        /** @type {string|null} */
        let cachedWidth = null;
        /** @type {string|null} */
        let cachedLeft = null;

        const updatePacContainerSize = () => {
            const rect = searchWrapper.getBoundingClientRect();
            cachedWidth = rect.width + 'px';
            cachedLeft = rect.left + window.scrollX + 'px';
        };

        updatePacContainerSize();

        const applyPacContainerStyles = () => {
            const pacContainers = document.querySelectorAll('.pac-container');
            if (pacContainers.length > 1) {
                for (let i = 0; i < pacContainers.length - 1; i++) {
                    const container = pacContainers[i];
                    if (container) container.remove();
                }
            }
            const pacContainer = document.querySelector('.pac-container');
            if (pacContainer instanceof HTMLElement && cachedWidth !== null && cachedLeft !== null) {
                pacContainer.style.setProperty('width', cachedWidth, 'important');
                pacContainer.style.setProperty('min-width', cachedWidth, 'important');
                pacContainer.style.setProperty('max-width', cachedWidth, 'important');
                pacContainer.style.setProperty('left', cachedLeft, 'important');
            }
        };

        const observer = new MutationObserver(applyPacContainerStyles);
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });

        window.addEventListener('resize', () => {
            updatePacContainerSize();
            applyPacContainerStyles();
        });

        window.addEventListener('scroll', applyPacContainerStyles);
    }

    /**
     * Initialize "Use My Location" button
     */
    initUseMyLocation() {
        const btn = document.querySelector(this.selectors.search.useMyLocationBtn);
        if (!btn || !navigator.geolocation || !this.isGoogleMapsAvailable) {
            if (btn && btn instanceof HTMLElement) {
                btn.style.display = 'none';
            }
            return;
        }

        btn.addEventListener('click', () => {
            this.getCurrentLocation();
        });
    }

    /**
     * Initialize expand button for geolocation
     */
    initExpandButton() {
        const expandBtn = document.querySelector(this.selectors.search.expandBtn);
        if (!expandBtn) {
            console.warn('Expand button not found');
            return;
        }

        // Hide button if Google Maps is not available
        if (!this.isGoogleMapsAvailable) {
            if (expandBtn instanceof HTMLElement) {
                expandBtn.style.display = 'none';
            }
            return;
        }

        // Remove existing listeners to avoid duplicates
        const newExpandBtn = expandBtn.cloneNode(true);
        if (expandBtn.parentNode) {
            expandBtn.parentNode.replaceChild(newExpandBtn, expandBtn);
        }

        newExpandBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.isGoogleMapsAvailable) {
                this.getCurrentLocation();
            }
        });
    }

    /**
     * Get current location using geolocation API
     */
    async getCurrentLocation() {
        const inputEl = document.querySelector(this.selectors.search.input);
        if (!(inputEl instanceof HTMLInputElement) || !navigator.geolocation) {
            console.warn('Geolocation not supported or input not found');
            return;
        }

        const isGoogleMapsReady = await this.waitForGoogleMaps();
        if (!isGoogleMapsReady) {
            console.warn('Google Maps API not available');
            return;
        }

        const originalPlaceholder = inputEl.placeholder;
        inputEl.placeholder = 'Getting your location...';
        inputEl.disabled = true;

        try {
            const position = await this.getGeolocationPosition();
            const { latitude: lat, longitude: lng } = position.coords;

            const googleMaps = getWindow().google;
            const geocoder = new googleMaps.maps.Geocoder();
            const geocodeResult = await this.reverseGeocodeWithGoogleMaps(geocoder, lat, lng);

            if (geocodeResult.formatted_address) {
                inputEl.value = geocodeResult.formatted_address;
            } else if (geocodeResult.name) {
                inputEl.value = geocodeResult.name;
            }

            inputEl.disabled = false;
            inputEl.placeholder = originalPlaceholder;

            this.setUserLocation(lat, lng);
        } catch (error) {
            console.error('Error getting location:', error);
            inputEl.disabled = false;
            inputEl.placeholder = originalPlaceholder;
            this.handleGeolocationError(error);
        }
    }

    /**
     * Wait for Google Maps API to be ready
     */
    async waitForGoogleMaps() {
        const win = getWindow();
        if (typeof win.google !== 'undefined' && win.google?.maps?.Geocoder) {
            return true;
        }

        const maxWait = 5000;
        const startTime = Date.now();
        while (Date.now() - startTime < maxWait) {
            if (typeof win.google !== 'undefined' && win.google?.maps?.Geocoder) {
                return true;
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        return false;
    }

    /**
     * Get geolocation position
     */
    getGeolocationPosition() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                resolve,
                reject,
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        });
    }

    /**
     * Reverse geocode using Google Maps
     */
    /**
     * @param {any} geocoder
     * @param {number} lat
     * @param {number} lng
     */
    reverseGeocodeWithGoogleMaps(geocoder, lat, lng) {
        return new Promise((resolve, reject) => {
            const win = getWindow();
            geocoder.geocode({ location: { lat, lng } }, (/** @type {any} */ results, /** @type {any} */ status) => {
                if (status === win.google.maps.GeocoderStatus.OK && results?.[0]) {
                    resolve(results[0]);
                } else if (status === win.google.maps.GeocoderStatus.REQUEST_DENIED) {
                    reject(new Error('REQUEST_DENIED'));
                } else if (status === win.google.maps.GeocoderStatus.OVER_QUERY_LIMIT) {
                    reject(new Error('OVER_QUERY_LIMIT'));
                } else if (status === win.google.maps.GeocoderStatus.ZERO_RESULTS) {
                    reject(new Error('ZERO_RESULTS'));
                } else {
                    reject(new Error(`Geocoding failed: ${status}`));
                }
            });
        });
    }

    /**
     * Handle geolocation errors
     */
    /**
     * @param {any} error
     */
    handleGeolocationError(error) {
        if (error.code === 1 || error.code === error.PERMISSION_DENIED) {
            console.warn('Location permission denied');
        } else if (error.code === 2 || error.code === error.POSITION_UNAVAILABLE) {
            console.warn('Location information unavailable');
        } else if (error.code === 3 || error.code === error.TIMEOUT) {
            console.warn('Location request timeout');
        } else if (error.message?.includes('REQUEST_DENIED')) {
            console.error('Google Maps API key error (REQUEST_DENIED)');
        } else if (error.message?.includes('OVER_QUERY_LIMIT')) {
            console.warn('Google Maps API quota exceeded');
        } else {
            console.warn('Unknown geolocation error:', error.message);
        }
    }

    /**
     * Set user location and search stores
     */
    /**
     * @param {number} lat
     * @param {number} lng
     */
    setUserLocation(lat, lng) {
        // Don't use location-based search if Google Maps is unavailable
        if (!this.isGoogleMapsAvailable) {
            console.warn('Cannot set user location: Google Maps unavailable');
            return;
        }

        this.fetchStoreList(lat, lng).then((response) => {
            getWindow().STORE_LOCATIONS = response?.data;
            this.renderStores();
            this.renderStoreResults(response?.data.slice(0, this.maxResults));

            const google = getWindow().google;
            if (!google || !google.maps || !this.map) {
                return;
            }
            const bounds = new google.maps.LatLngBounds();
            this.storeMarkers.forEach(marker => {
                bounds.extend(marker.getPosition());
            });
            if (!bounds.isEmpty()) {
                this.map.fitBounds(bounds);
            } else {
                const settings = getWindow().GOOGLE_MAP_SETTINGS || {};
                this.map.setCenter({
                    lat: Number(settings.defaultLat),
                    lng: Number(settings.defaultLng)
                });
                this.map.setZoom(Number(settings.defaultZoom));
            }
        });
    }

    /**
     * Load map configuration (radius, pins, brand/type configs)
     */
    /**
     * @param {GoogleMapSettings | any} [settingsOverride]
     */
    loadMapSettings(settingsOverride) {
        const settings = settingsOverride || (getWindow().GOOGLE_MAP_SETTINGS || {});

        this.radiusKm = Number(settings.radiusKm) || 200;
        this.maxResults = Number(settings.maxResults) || 20;
        this.enableBrandPins = Boolean(settings.enableBrandPins) || false;
        this.brandConfigs = settings.brandConfigs || [];
        this.typeConfigs = settings.typeConfigs || [];
        this.defaultPinImage = settings.defaultPinImage || settings.pinIcon || null;
    }

    /**
     * Get brand config for pin icon
     */
    /**
     * @param {string} brandName
     */
    getBrandConfig(brandName) {
        if (!this.enableBrandPins || !brandName || !this.brandConfigs.length) {
            return null;
        }

        const cleanBrandName = brandName.toString().trim();

        let config = this.brandConfigs.find(brand =>
            brand.name.toLowerCase() === cleanBrandName.toLowerCase()
        );

        if (!config) {
            config = this.brandConfigs.find(brand =>
                cleanBrandName.toLowerCase().includes(brand.name.toLowerCase()) ||
                brand.name.toLowerCase().includes(cleanBrandName.toLowerCase())
            );
        }

        return config;
    }

    /**
     * Build Google Maps marker icon config
     */
    /**
     * @param {string} iconUrl
     */
    buildMarkerIcon(iconUrl) {
        const googleMaps = getWindow().google;
        if (!googleMaps || !googleMaps.maps || !iconUrl) {
            return undefined;
        }

        return {
            url: iconUrl,
            scaledSize: new googleMaps.maps.Size(40, 40),
            anchor: new googleMaps.maps.Point(20, 40)
        };
    }

    /**
     * Get pin icon config for store
     * Priority:
     * 1. Store type specific pin (overrides brand)
     * 2. Brand-specific pin
     * 3. Default pin image / fallback
     */
    /**
     * @param {any} store
     */
    getPinForStore(store) {
        const storeBrands = this.getStoreBrands(store);

        // Store type can come from metafields or top-level field
        const storeType =
            (store.metafields && store.metafields.store_type && store.metafields.store_type.value) ||
            (store.store_type && store.store_type.value) ||
            store.storeType ||
            null;

        // 1. Store type specific pin (highest priority)
        if (storeType && Array.isArray(this.typeConfigs) && this.typeConfigs.length) {
            const normalizedType = String(storeType).trim().toLowerCase();
            const typeConfig = this.typeConfigs.find((t) => {
                if (!t || !t.name) return false;
                const typeName = String(t.name).trim().toLowerCase();
                return typeName === normalizedType;
            });

            if (typeConfig && typeConfig.pinImage) {
                const typeIcon = this.buildMarkerIcon(typeConfig.pinImage);
                if (typeIcon) {
                    return typeIcon;
                }
            }
        }

        // 2. Brand-specific pin (fallback if no type pin)
        if (this.enableBrandPins && storeBrands.length && Array.isArray(this.brandConfigs) && this.brandConfigs.length) {
            for (const brand of storeBrands) {
                const brandConfig = this.getBrandConfig(brand);
                if (brandConfig && brandConfig.pinImage) {
                    const brandIcon = this.buildMarkerIcon(brandConfig.pinImage);
                    if (brandIcon) {
                        return brandIcon;
                    }
                }
            }
        }

        // 3. Default pin image / fallback to settings.pinIcon
        const settings = getWindow().GOOGLE_MAP_SETTINGS || {};
        const fallbackUrl = this.defaultPinImage || settings.defaultPinImage || settings.pinIcon || null;

        const fallbackIcon = this.buildMarkerIcon(fallbackUrl);
        if (fallbackIcon) {
            return fallbackIcon;
        }

        // As a final safeguard, let Google use its default marker
        return undefined;
    }

    /**
     * Get store brands from store data
     */
    /**
     * @param {any} store
     */
    getStoreBrands(store) {
        const brand = store.brands;
        if (!brand) return [];
        if (Array.isArray(brand)) return brand;
        try {
            const parsed = JSON.parse(brand.value);
            if (Array.isArray(parsed)) return parsed;
            return [brand];
        } catch (e) {
            return [brand];
        }
    }

    /**
     * Close any open marker info windows
     */
    closeAllInfoWindows() {
        this.storeMarkers.forEach(m => {
            if (m.infoWindow) m.infoWindow.close();
        });
    }

    /**
     * Render stores on map
     */
    renderStores() {
        // Don't render on map if Google Maps is unavailable
        if (!this.isGoogleMapsAvailable || !this.map) {
            return;
        }

        const stores = this.applyFilters((/** @type {any} */ (getWindow().STORE_LOCATIONS)) || []);
        this.storeMarkers.forEach(m => m.setMap(null));
        this.storeMarkers = [];
        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
        }
        const googleMaps = getWindow().google;
        if (!googleMaps || !googleMaps.maps) {
            return;
        }
        const bounds = new googleMaps.maps.LatLngBounds();

        stores.forEach(store => {
            const lat = store.address?.latitude;
            const lng = store.address?.longitude;
            if (!lat || !lng) return;
            const pinConfig = this.getPinForStore(store);

            const marker = new googleMaps.maps.Marker({
                position: { lat, lng },
                title: store.name,
                icon: pinConfig
            });

            const storeBrand = store.brand ||
                store.store_brand ||
                store.metafields?.brand?.value ||
                store.brand_name ||
                store.brandName;

            marker.storeData = {
                id: store.id,
                name: store.name,
                brand: storeBrand,
                address: this.formatStoreAddress(store.address),
                lat,
                lng
            };

            const detailUrl = this.getStoreDetailUrl(store);
            const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
            const phone = store.address?.phone || '';

            const infoWindow = new googleMaps.maps.InfoWindow({
                content: `
                    <div class="store-info-window">
                        ${storeBrand ? `<div class="store-brand">${storeBrand}</div>` : ''}
                        <h3 class="store-info-window-name">${store.name}</h3>
                        <p class="store-info-window-address">${this.formatStoreAddress(store.address)}</p>
                        ${phone ? `<p class="store-info-window-phone">${phone}</p>` : ''}
                        <div class="store-info-window-links">
                            <a href="${detailUrl}" class="store-info-window-link view-details">View Details</a>
                            <span class="separator">|</span>
                            <a href="${directionsUrl}" target="_blank" class="store-info-window-link directions">Directions</a>
                        </div>
                    </div>
                `
            });

            marker.addListener('click', () => {
                this.closeAllInfoWindows();
                infoWindow.open(this.map, marker);
                marker.infoWindow = infoWindow;
            });

            this.storeMarkers.push(marker);
            bounds.extend(marker.getPosition());
        });

        const markerClustererLib = /** @type {any} */ (getWindow().markerClusterer);
        this.markerCluster = new markerClustererLib.MarkerClusterer({
            map: this.map,
            markers: this.storeMarkers
        });

        if (stores.length > 0 && !bounds.isEmpty()) {
            this.map.fitBounds(bounds);
        } else {
            const settings = getWindow().GOOGLE_MAP_SETTINGS || {};
            this.map.setCenter({
                lat: Number(settings.defaultLat) || -25.2744,
                lng: Number(settings.defaultLng) || 133.7751
            });
            this.map.setZoom(Number(settings.defaultZoom) || 5);
        }
    }

    /**
     * Render store results in list
     */
    /**
     * @param {any[]} stores
     */
    renderStoreResults(stores) {
        const container = document.querySelector(this.selectors.list.storeList);
        const countHeader = document.querySelector(this.selectors.list.countHeader);
        if (!container) return;

        let filteredStores = this.applyFilters(stores);

        // If Google Maps is unavailable, sort alphabetically
        if (!this.isGoogleMapsAvailable) {
            filteredStores = this.sortStoresAlphabetically(filteredStores);
        }

        container.innerHTML = '';

        if (countHeader) {
            countHeader.innerHTML = '';
            countHeader.textContent = `Displaying ${filteredStores.length} Stores`;
        }

        filteredStores.forEach(store => {
            const openingText = this.getStoreOpeningText(store);
            const card = this.createStoreCard(store, openingText);

            const detailsLink = card.querySelector(this.selectors.list.storeDetailsLink);
            if (detailsLink) {
                const detailUrl = this.getStoreDetailUrl(store);
                detailsLink.setAttribute('href', detailUrl);
            }

            container.appendChild(card);
        });
    }

    /**
     * Get store opening text
     * @param {any} store
     */
    getStoreOpeningText(store) {
        const { open, until, hasSchedule } = getStoreStatus(store);
        if (!hasSchedule) return '';

        const openSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z" fill="#34C759"/>
                        <mask id="mask0_9400_26094" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                            <rect width="16" height="16" fill="#D9D9D9"/>
                        </mask>
                        <g mask="url(#mask0_9400_26094)">
                            <path d="M6.36752 11.5386L3.05469 8.22573L3.53035 7.75007L6.36752 10.5872L12.4714 4.4834L12.947 4.95906L6.36752 11.5386Z" fill="white"/>
                        </g>
                    </svg>`;

        const closedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z" fill="#830000"></path>
                        <path d="M4.26669 12.2052L3.79486 11.7333L7.52819 8L3.79486 4.26666L4.26669 3.79483L8.00003 7.52816L11.7334 3.79483L12.2052 4.26666L8.47186 8L12.2052 11.7333L11.7334 12.2052L8.00003 8.47183L4.26669 12.2052Z" fill="white"></path>
                    </svg>`;

        if (open) {
            return `${openSvg}
                    <span class="status-text">
                        <strong>Open</strong>${until ? ' until ' + until : ''}
                    </span>`;
        }

        return `${closedSvg}
                    <span class="status-text">
                        <strong>Closed</strong>${until ? ' until ' + until : ''}
                    </span>`;
    }

    /**
     * Create store card element
     */
    /**
     * @param {any} store
     * @param {string} storeStatusContent
     */
    createStoreCard(store, storeStatusContent) {
        const card = document.createElement('div');
        card.className = 'store-card';
        card.innerHTML = `
            <div class="store-card-top">
                <h3 class="store-name">${store.name}</h3>
                <span class="store-distance">${store.distanceKm ? store.distanceKm + ' km' : ''}</span>
            </div>
            <p class="store-address">${this.formatStoreAddress(store.address)}</p>
            <div class="store-card-bottom">
                <div class="store-status">${storeStatusContent}</div>
                <a href="#" class="store-details-link">
                    <span>More Details</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <mask id="mask0_12003_33476" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                            <rect width="24" height="24" fill="#B3A458"/>
                        </mask>
                        <g mask="url(#mask0_12003_33476)">
                            <path d="M17.0788 12.5H5V11.5H17.0788L11.2865 5.70775L12 5L19 12L12 19L11.2865 18.2923L17.0788 12.5Z" fill="#B3A458"/>
                        </g>
                    </svg>
                </a>
            </div>
        `;
        return card;
    }

    /**
     * Navigate to detail page
     */
    /**
     * @param {any} store
     */
    navigateToDetailPage(store) {
        const detailUrl = this.getStoreDetailUrl(store);
        if (detailUrl !== '#') {
            window.location.href = detailUrl;
        }
    }

    /**
     * Get store detail URL
     * @param {any} store
     */
    getStoreDetailUrl(store) {
        const storeUrl = store.storePage?.reference?.handle;

        if (!storeUrl) return '#';

        const pageUrlEl = document.querySelector(this.selectors.data.pageUrl);
        let pageUrl = (pageUrlEl && pageUrlEl.getAttribute('data-store-locator-page-url')) ||
            (getWindow().STORE_LOCATOR_PAGE_URL || window.location.pathname);

        if (pageUrl) {
            const domain = window.location.origin || `https://${Shopify?.shop}`;
            const finalPath = `/pages/${pageUrl}/${storeUrl}`;

            return domain + finalPath;
        }

        return '#';
    }

    /**
     * Apply filters to stores
     */
    /**
     * @param {any[]} stores
     */
    applyFilters(stores) {
        const checkboxes = document.querySelectorAll(this.selectors.search.filterCheckboxes + ':checked');
        const checked = Array.from(checkboxes)
            .map(cb => {
                if (cb instanceof HTMLInputElement) {
                    return cb.value;
                }
                return '';
            });

        if (checked.length === 0) return stores;

        return stores.filter(store => {
            const stockists = this.getStockists(store);
            return stockists.some((/** @type {any} */ s) => checked.includes(s));
        });
    }

    /**
     * Get stockists from store data
     */
    /**
     * @param {any} store
     */
    getStockists(store) {
        if (!store.stockist?.value) return [];
        try {
            return JSON.parse(store.stockist.value);
        } catch (e) {
            console.error('Invalid stockist value', e);
            return [];
        }
    }

    /**
     * Fetch store list from API
     */
    /**
     * @param {number} [lat]
     * @param {number} [lng]
     */
    fetchStoreList(lat, lng) {
        return new Promise(async (resolve, reject) => {
            try {
                const baseUrl = `${window.location.origin}/apps/service/shopify/store-list`;
                const params = new URLSearchParams();

                if (lat && lng) {
                    params.append('lat', String(lat));
                    params.append('lng', String(lng));
                    params.append('maxDistance', String(this.radiusKm));
                }

                const apiUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

                const response = await fetch(apiUrl, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`API returned ${response.status}`);
                }

                const data = await response.json();
                const stockistFilterGrid = document.querySelector('[data-stockist-filter-grid]');
                if (stockistFilterGrid) {
                    const raw = data?.options?.response?.stockist?.edges?.[0]?.node?.validations?.[0]?.value;
                    const list = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch (_) { return []; } })() : (Array.isArray(raw) ? raw : []);
                    if (list.length) {
                        stockistFilterGrid.innerHTML = list.map(stockist => {
                            const s = String(stockist).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
                            return `<label class="filter-item"><input type="checkbox" value="${s}"> <span>${s}</span></label>`;
                        }).join('');
                    }
                }
                resolve(data);

            } catch (err) {
                console.error('Fetch nearest store error:', err);
                resolve(null);
            }
        });
    }

    // ==================== Detail View Functions ====================

    /**
     * Initialize detail view
     */
    initDetailView(metaobjectId) {
        // Ensure map and pin settings are loaded for detail view
        this.loadMapSettings();

        this.showDetailView();
        this.fetchStoreDetail(metaobjectId);
    }

    /**
     * Show list view, hide detail view
     */
    showListView() {
        this.toggleDetailSections(false);
        const detailEl = document.getElementById('store-locator-detail');
        const listEl = document.getElementById('store-locator-list');
        if (detailEl) detailEl.style.display = 'none';
        if (listEl) listEl.style.display = 'block';
    }

    /**
     * Show detail view, hide list view
     */
    showDetailView() {
        this.toggleDetailSections(true);
        const listEl = document.getElementById('store-locator-list');
        const detailEl = document.getElementById('store-locator-detail');
        const detailLoading = document.getElementById('store-detail-loading');
        const detailContent = document.getElementById('store-detail-content');
        const detailError = document.getElementById('store-detail-error');

        if (listEl) listEl.style.display = 'none';
        if (detailEl) detailEl.style.display = 'block';
        if (detailLoading) detailLoading.style.display = 'block';
        if (detailContent) detailContent.style.display = 'none';
        if (detailError) detailError.style.display = 'none';
    }

    /**
     * Fetch store detail from API
     */
    /**
     * @param {string} metaobjectId
     */
    fetchStoreDetail(metaobjectId) {
        const pageUrlEl = document.querySelector(this.selectors.data.pageUrl);
        const pageUrl = (pageUrlEl && pageUrlEl.getAttribute('data-store-locator-page-url')) ||
            (getWindow().STORE_LOCATOR_PAGE_URL || '');
        const apiBase = (getWindow().STORE_LOCATOR_API_BASE || null) || (window.location.origin + '/apps/service/shopify');
        const url = apiBase + '/store-locator-detail?id=' + encodeURIComponent(metaobjectId);

        fetch(url)
            .then((r) => r.json())
            .then((res) => {
                const loc = (res && res.data) ? res.data : res;
                if (loc && (loc.id || loc.name || loc.address)) {
                    this.renderDetailView(loc);
                } else {
                    this.showDetailError();
                }
            })
            .catch(() => {
                this.showDetailError();
            });
    }

    /**
     * Show detail error message
     */
    showDetailError() {
        const detailLoading = document.getElementById('store-detail-loading');
        const detailError = document.getElementById('store-detail-error');
        if (detailLoading) detailLoading.style.display = 'none';
        if (detailError) detailError.style.display = 'block';
    }

    /**
     * Toggle sections that should be hidden in detail view
     */
    /**
     * @param {boolean} showDetail
     */
    toggleDetailSections(showDetail) {
        const nodes = document.querySelectorAll(this.selectors.data.hideOnDetail);
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if (node instanceof HTMLElement) {
                node.style.display = showDetail ? 'none' : '';
            }
        }
    }

    /**
     * Render detail view with store data
     */
    /**
     * @param {any} loc
     */
    renderDetailView(loc) {
        const detailEl = document.getElementById('store-locator-detail');
        const detailLoading = document.getElementById('store-detail-loading');
        const detailContent = document.getElementById('store-detail-content');
        const detailError = document.getElementById('store-detail-error');

        if (!detailEl) return;

        detailEl.style.display = 'block';
        if (detailLoading) detailLoading.style.display = 'none';
        if (detailContent) detailContent.style.display = 'block';
        if (detailError) detailError.style.display = 'none';

        this.renderDetailName(loc);
        this.renderDetailBreadcrumb(loc);
        this.renderDetailAddress(loc);
        this.renderDetailContact(loc);
        this.renderDetailHours(loc);
        this.renderSpecialHours(loc);
        this.renderDetailStatus(loc);
        this.renderDetailAbout(loc);
        this.renderDetailManager(loc);
        this.renderDetailMap(loc);
        this.renderStockists(loc);
        this.renderStructuredData(loc);
    }

    /**
     * Render structured data (JSON-LD) schema for Store Details page
     * @param {any} loc
     */
    renderStructuredData(loc) {
        if (!loc) return;

        const storeUrlFromLoc = this.getStoreDetailUrl(loc);
        let storeUrl = (storeUrlFromLoc && storeUrlFromLoc !== '#')
            ? storeUrlFromLoc
            : (window.location.origin + window.location.pathname);

        const telephone = loc.address?.phone || loc.phone || loc.telephone || '';
        const email = (loc.contactEmail && typeof loc.contactEmail === 'object')
            ? (loc.contactEmail.value || '')
            : (loc.contactEmail || loc.email || loc.contact_email || '');

        const addr = loc.address || {};
        const streetAddress = [addr.address1, addr.address2].filter(Boolean).join(', ') || addr.streetAddress || addr.street || '';
        const addressLocality = addr.city || addr.suburb || addr.addressLocality || '';
        const addressRegion = addr.province || addr.state || addr.addressRegion || '';
        const postalCode = addr.zip || addr.postcode || addr.postalCode || '';
        const addressCountry = addr.country || addr.addressCountry || 'AU';

        /** @type {any} */
        const postalAddress = {
            '@type': 'PostalAddress'
        };
        if (streetAddress) postalAddress.streetAddress = streetAddress;
        if (addressLocality) postalAddress.addressLocality = addressLocality;
        if (addressRegion) postalAddress.addressRegion = addressRegion;
        if (postalCode) postalAddress.postalCode = postalCode;
        if (addressCountry) postalAddress.addressCountry = addressCountry;

        const rawHours = this.parseHours(loc.openingHours);
        const openingHoursSpec = [];

        if (Array.isArray(rawHours) && rawHours.length > 0) {
            /** @type {Record<number, string>} */
            const dayMap = {
                1: 'Monday',
                2: 'Tuesday',
                3: 'Wednesday',
                4: 'Thursday',
                5: 'Friday',
                6: 'Saturday',
                0: 'Sunday'
            };

            const formatTime = (t) => {
                if (!t) return '';
                const parts = String(t).trim().split(':');
                if (parts.length >= 2) {
                    const hh = parts[0].padStart(2, '0');
                    const mm = parts[1].padStart(2, '0');
                    return `${hh}:${mm}`;
                }
                return String(t);
            };

            const weeklyHours = rawHours.filter(h => !h.date || String(h.date).trim() === '');
            /** @type {Record<string, { opens: string, closes: string, days: string[] }>} */
            const groupedSpecs = {};

            weeklyHours.forEach(h => {
                const dayName = dayMap[h.day_of_week];
                if (dayName && h.start_time && h.end_time) {
                    const opens = formatTime(h.start_time);
                    const closes = formatTime(h.end_time);
                    const key = `${opens}_${closes}`;

                    if (!groupedSpecs[key]) {
                        groupedSpecs[key] = {
                            opens,
                            closes,
                            days: []
                        };
                    }
                    if (!groupedSpecs[key].days.includes(dayName)) {
                        groupedSpecs[key].days.push(dayName);
                    }
                }
            });

            Object.values(groupedSpecs).forEach(group => {
                openingHoursSpec.push({
                    '@type': 'OpeningHoursSpecification',
                    'dayOfWeek': group.days.length === 1 ? group.days[0] : group.days,
                    'opens': group.opens,
                    'closes': group.closes
                });
            });
        }

        let hasMap = loc.hasMap || addr.mapUrl || loc.mapUrl || '';
        if (!hasMap && addr.latitude && addr.longitude) {
            hasMap = `https://www.google.com/maps/dir/?api=1&destination=${addr.latitude},${addr.longitude}`;
        }

        const storeBrand = loc.brand || loc.store_brand || loc.brand_name || loc.brandName || 'Betta Home Living';
        const brandUrl = window.location.origin ? (window.location.origin.replace(/\/$/, '') + '/') : 'https://betta.com.au/';

        /** @type {any} */
        const schemaData = {
            '@context': 'https://schema.org',
            '@type': 'ElectronicsStore',
            '@id': `${storeUrl.trim()}#store`,
            'name': loc.name || 'Store',
            'url': storeUrl.trim()
        };

        if (telephone) schemaData.telephone = telephone;
        if (email) schemaData.email = email;
        if (Object.keys(postalAddress).length > 1) schemaData.address = postalAddress;
        if (openingHoursSpec.length > 0) schemaData.openingHoursSpecification = openingHoursSpec;
        if (hasMap) schemaData.hasMap = hasMap;
        schemaData.brand = {
            '@type': 'Brand',
            'name': storeBrand,
            'url': brandUrl
        };

        let scriptTag = document.getElementById('store-jsonld');
        if (!scriptTag) {
            scriptTag = document.createElement('script');
            scriptTag.id = 'store-jsonld';
            scriptTag.type = 'application/ld+json';
            document.head.appendChild(scriptTag);
        }
        scriptTag.textContent = JSON.stringify(schemaData, null, 2);
    }

    renderStockists(loc) {
        let stockListData = [];
        if (loc.stockist?.value && typeof loc.stockist?.value === 'string') {
            try {
                stockListData = JSON.parse(loc.stockist.value);
            } catch (e) {
                console.error('Invalid stockist JSON', e);
            }
        } else if (Array.isArray(loc.stockist?.value)) {
            stockListData = loc.stockist?.value;
        }

        let stockistsEl = document.getElementById('store-detail-available');
        if (stockistsEl) {
            if (Array.isArray(stockListData) && stockListData.length > 0) {
                stockistsEl.innerHTML = stockListData.map(item => {
                    return '<span>' + String(item) + '</span>';
                }).join('');
                stockistsEl.closest('.store-locator__available').style.display = 'grid';
            } else {
                stockistsEl.innerHTML = '';
                stockistsEl.closest('.store-locator__available').style.display = 'none';
            }
        }
    }

    /**
     * Render store name in detail view
     */
    /**
     * @param {any} loc
     */
    renderDetailName(loc) {
        const nameEl = document.getElementById('store-detail-name');
        if (nameEl) nameEl.textContent = loc.name || 'Store';

        const nameElAvailable = document.getElementById('store-detail-name-available');
        if (nameElAvailable) nameElAvailable.textContent = loc.name || 'Store';

        const nameElAbout = document.getElementById('store-detail-name-about');
        if (nameElAbout) nameElAbout.textContent = loc.name || 'Store';
    }



    /**
     * Render breadcrumb with store name
     */
    /**
     * @param {any} loc
     */
    renderDetailBreadcrumb(loc) {
        const breadcrumbStoreEl = document.getElementById('store-detail-breadcrumb-store');
        if (breadcrumbStoreEl) {
            const storeName = loc.name || 'Store';
            const breadcrumbUrlEl = breadcrumbStoreEl.querySelector('.breadcrumb--url');
            if (breadcrumbUrlEl) {
                breadcrumbUrlEl.textContent = storeName;
            }
        }
    }

    /**
     * Render store address in detail view
     */
    /**
     * @param {any} loc
     */
    renderDetailAddress(loc) {
        const addr = loc.address;
        let addrHtml = '';
        if (addr && addr.formatted && addr.formatted.length) {
            addrHtml = addr.formatted.join(', ');
        } else if (addr) {
            addrHtml = [addr.address1, addr.address2, addr.city, addr.province, addr.zip, addr.country].filter(Boolean).join(', ');
        }
        const addrEl = document.getElementById('store-detail-address');
        if (addrEl) addrEl.textContent = addrHtml;
    }

    /**
     * Render contact information (phone, email, contact button)
     */
    /**
     * @param {any} loc
     */
    renderDetailContact(loc) {
        const phoneEl = document.getElementById('store-detail-phone');
        const emailEl = document.getElementById('store-detail-email');
        const btnEl = document.getElementById('store-detail-contact-btn');
        const phoneWrapper = phoneEl ? phoneEl.closest('.store-details__phone') : null;
        const emailWrapper = emailEl ? emailEl.closest('.store-details__email') : null;
        const addr = loc.address;

        if (phoneWrapper) phoneWrapper.style.display = 'none';
        if (emailWrapper) emailWrapper.style.display = 'none';
        if (btnEl) btnEl.style.display = 'none';

        if (addr && addr.phone) {
            if (phoneEl && phoneWrapper) {
                phoneEl.innerHTML = '<a href="tel:' + addr.phone.replace(/"/g, '&quot;') + '">' + (addr.phone.replace(/</g, '&lt;').replace(/>/g, '&gt;')) + '</a>';
                phoneWrapper.style.display = 'flex';
            }
            if (btnEl instanceof HTMLAnchorElement) {
                btnEl.href = 'mailto:' + loc.contactEmail.value.replace(/"/g, '&quot;');
                btnEl.style.display = 'inline-flex';
            }
        }
        if (loc.contactEmail && loc.contactEmail.value && emailEl && emailWrapper) {
            emailEl.innerHTML = '<a href="mailto:' + loc.contactEmail.value.replace(/"/g, '&quot;') + '">' + (loc.contactEmail.value.replace(/</g, '&lt;').replace(/>/g, '&gt;')) + '</a>';
            emailEl.style.display = 'block';
            emailWrapper.style.display = 'flex';
        }
    }

    /**
     * Render opening hours table
     */
    /**
     * @param {any} loc
     */
    renderDetailHours(loc) {
        const hours = this.parseHours(loc.openingHours);
        const hoursEl = document.getElementById('store-detail-hours');
        const tbody = hoursEl ? hoursEl.querySelector('tbody') : null;
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        if (tbody) {
            if (hours && Array.isArray(hours)) {
                tbody.innerHTML = days.map((day, i) => {
                    // Monday..Saturday => 1..6, Sunday => 0
                    const dayOfWeek = i === 6 ? 0 : i + 1;
                    const d = hours.find((/** @type {any} */ h) => h.day_of_week === dayOfWeek);
                    if (d) return '<tr><td style="font-weight: 500;">' + day + '</td><td>' + this.fmtTime(d.start_time) + ' – ' + this.fmtTime(d.end_time) + '</td></tr>';
                    return '<tr><td style="font-weight: 500;">' + day + '</td><td>Closed</td></tr>';
                }).join('');
            } else {
                tbody.innerHTML = '<tr><td colspan="2">Hours not available</td></tr>';
            }
        }
    }

    renderSpecialHours(loc) {
        const hours = this.parseHours(loc.openingHours);
        const tableEl = document.getElementById('store-detail-special-hours');
        if (!tableEl) return;

        const now = new Date();
        const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');

        const specials = Array.isArray(hours)
            ? hours.filter((/** @type {any} */ h) => {
                if (!h || !h.date || String(h.date).trim() === '') return false;
                const datePart = String(h.date).split(' ')[0];
                return datePart >= todayStr;
            })
            : [];

        if (specials.length === 0) {
            tableEl.innerHTML = '';
            return;
        }

        const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const rows = specials.map((/** @type {any} */ item) => {
            const datePart = String(item.date).split(' ')[0];
            const [y, m, d] = datePart.split('-').map(Number);
            const dt = new Date(y, m - 1, d);

            const weekday = weekdays[dt.getDay()];
            const dateStr = d + ' ' + months[m - 1];

            const hasHours = item.start_time && item.end_time;
            const hoursStr = hasHours
                ? this.fmtTime(item.start_time) + ' – ' + this.fmtTime(item.end_time)
                : 'Closed';

            return '<tr>'
                + '<td class="opening-row weekday">' + weekday + '</td>'
                + '<td class="opening-row date">' + dateStr + '</td>'
                + '<td class="opening-row hours">' + hoursStr + '</td>'
                + '</tr>';
        }).join('');

        tableEl.innerHTML = '<tbody>' + rows + '</tbody>';
    }

    /**
     * Render store status (open/closed)
     * @param {any} loc
     */
    renderDetailStatus(loc) {
        const statusEl = document.getElementById('store-detail-status');
        const statusText = document.getElementById('store-detail-status-text');
        const dot = statusEl ? statusEl.querySelector('.store-locator__status-dot') : null;

        if (!statusEl) return;

        const { open, until, hasSchedule } = getStoreStatus(loc);

        if (hasSchedule && statusText && dot) {
            const checkedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z" fill="#34C759"/>
            <mask id="mask0_10993_25419" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
            <rect width="16" height="16" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_10993_25419)">
            <path d="M6.36752 11.5386L3.05469 8.22573L3.53035 7.75007L6.36752 10.5872L12.4714 4.4834L12.947 4.95906L6.36752 11.5386Z" fill="white"/>
            </g>
            </svg>`;

            const closedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z" fill="#830000"/>
            <path d="M4.26669 12.2052L3.79486 11.7333L7.52819 8L3.79486 4.26666L4.26669 3.79483L8.00003 7.52816L11.7334 3.79483L12.2052 4.26666L8.47186 8L12.2052 11.7333L11.7334 12.2052L8.00003 8.47183L4.26669 12.2052Z" fill="white"/>
            </svg>`;

            statusEl.style.display = 'flex';
            dot.className = 'store-locator__status-dot ' + (open ? 'store-locator__status-dot--open' : 'store-locator__status-dot--closed');
            dot.innerHTML = open ? checkedSvg : closedSvg;
            statusText.innerHTML = open
                ? ('<strong>Open</strong>' + (until ? ' until ' + until : ''))
                : ('<strong>Closed</strong>' + (until ? ' until ' + until : ''));
        } else {
            statusEl.style.display = 'none';
        }
    }

    /**
     * Render about section - only storeNotes
     */
    /**
     * @param {any} loc
     */
    renderDetailAbout(loc) {
        const aboutHtml = this.renderStoreNotesHtml(loc.storeNotes);

        const aboutEl = document.querySelector(this.selectors.detail.about);
        const detailRight = aboutEl ? aboutEl.closest('.store-locator__about') : null;

        if (aboutEl) {
            aboutEl.innerHTML = aboutHtml || '';
        }

        if (detailRight instanceof HTMLElement) {
            detailRight.style.display = aboutHtml ? 'block' : 'none';
        }
    }

    /**
     * Render store manager
     */
    /**
     * @param {any} loc
     */
    renderDetailManager(loc) {
        const managerEl = document.getElementById('store-detail-manager');
        const headerRight = managerEl ? managerEl.closest('.store-locator__detail-header-right') : null;

        if (managerEl) {
            if (loc.storeManager && loc.storeManager.value) {
                managerEl.textContent = loc.storeManager.value;
                if (headerRight instanceof HTMLElement) {
                    headerRight.classList.remove('hidden');
                }
            } else {
                managerEl.textContent = '';
                if (headerRight instanceof HTMLElement) {
                    headerRight.classList.add('hidden');
                }
            }
        }
    }

    /**
     * Render detail map
     */
    /**
     * @param {any} loc
     */
    renderDetailMap(loc) {
        const addr = loc.address;
        if (addr && addr.latitude != null && addr.longitude != null) {
            // Use same pin selection logic as list view (store_type + brand)
            const markerIcon = this.getPinForStore(loc);
            this.initDetailMap(addr.latitude, addr.longitude, loc.name, markerIcon, loc);
        } else {
            const mapEl = document.getElementById('store-detail-map');
            if (mapEl) {
                mapEl.innerHTML = '<p class="store-locator__map-fallback">Map not available</p>';
            }
        }
    }

    /**
     * Initialize detail map
     * @param {number|string} lat
     * @param {number|string} lng
     * @param {string} storeName
     * @param {any} [markerIcon]
     * @param {any} [loc]
     */
    initDetailMap(lat, lng, storeName, markerIcon, loc) {
        const mapEl = document.getElementById('store-detail-map');
        const win = getWindow();
        if (!mapEl || typeof win.google === 'undefined' || !win.google?.maps) {
            if (mapEl) {
                mapEl.innerHTML = '<p class="store-locator__map-fallback">Map temporarily unavailable</p>';
            }
            return;
        }

        // Check if map already exists
        const mapElAny = /** @type {any} */ (mapEl);
        if (mapElAny._mapInstance) {
            mapElAny._mapInstance.setCenter({ lat: parseFloat(String(lat)), lng: parseFloat(String(lng)) });
            return;
        }

        const googleMaps = win.google;
        this.detailMap = new googleMaps.maps.Map(mapEl, {
            zoom: 15,
            center: { lat: parseFloat(String(lat)), lng: parseFloat(String(lng)) },
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
        });

        mapElAny._mapInstance = this.detailMap;

        const marker = new googleMaps.maps.Marker({
            position: { lat: parseFloat(String(lat)), lng: parseFloat(String(lng)) },
            map: this.detailMap,
            title: storeName || 'Store',
            icon: markerIcon || undefined
        });

        if (loc) {
            const storeBrand = loc.brand ||
                loc.store_brand ||
                loc.metafields?.brand?.value ||
                loc.brand_name ||
                loc.brandName;
            const formattedAddress = this.formatStoreAddress(loc.address);
            const phone = loc.address?.phone || '';
            const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

            const infoWindow = new googleMaps.maps.InfoWindow({
                content: `
                    <div class="store-info-window">
                        ${storeBrand ? `<div class="store-brand">${storeBrand}</div>` : ''}
                        <h3 class="store-info-window-name">${storeName || 'Store'}</h3>
                        ${formattedAddress ? `<p class="store-info-window-address">${formattedAddress}</p>` : ''}
                        ${phone ? `<p class="store-info-window-phone">${phone}</p>` : ''}
                        <div class="store-info-window-links">
                            <a href="${directionsUrl}" target="_blank" class="store-info-window-link directions">Directions</a>
                        </div>
                    </div>
                `
            });

            marker.addListener('click', () => {
                infoWindow.open(this.detailMap, marker);
            });

            this.detailMap.addListener('click', () => {
                infoWindow.close();
            });
        }
    }

    // ==================== Utility Functions ====================

    /**
     * Format store address safely for UI display
     * @param {any} address
     * @returns {string}
     */
    formatStoreAddress(address) {
        if (!address) return '';
        if (typeof address === 'string') return address;
        if (Array.isArray(address?.formatted)) return address.formatted.join(', ');
        if (typeof address?.formatted === 'string') return address.formatted;
        if (typeof address === 'object') {
            const parts = [address.address1, address.address2, address.city, address.province, address.zip, address.country]
                .filter(Boolean);
            if (parts.length) return parts.join(', ');
            return Object.values(address).filter(v => typeof v === 'string').join(', ');
        }
        return '';
    }

    /**
     * Parse opening hours from JSON
     * @param {any} val
     */
    parseHours(val) {
        if (!val) return null;
        if (Array.isArray(val)) return val;
        return getRawHoursArray({ openingHours: val }) || getRawHoursArray(val);
    }

    /**
     * Parse JSON value
     * @param {any} val
     */
    parseJson(val) {
        if (!val) return null;
        try {
            const v = typeof val === 'string' ? val : (val.value || '');
            return v ? JSON.parse(v) : null;
        } catch (e) { return null; }
    }

    /**
     * Format time from 24h to 12h format
     * @param {string|undefined} t
     */
    fmtTime(t) {
        return sharedFmtTime(t);
    }

    /**
     * Render store notes HTML from JSON
     */
    /**
     * @param {any} storeNotes
     */
    renderStoreNotesHtml(storeNotes) {
        const raw = typeof storeNotes === 'string' ? storeNotes : (storeNotes && storeNotes.value);
        if (!raw) return '';
        try {
            const data = JSON.parse(raw);
            const children = data && data.children;
            if (!Array.isArray(children)) return '';
            const html = [];

            const parseInlineNode = (node) => {
                if (!node) return '';

                if (node.type === 'text') {
                    let text = node.value || '';

                    if (/^\s*<[^>]+>\s*$/i.test(text) || /^\s*(<br\s*\/?>)+\s*$/i.test(text)) {
                        return text;
                    }

                    if (node.bold && !text.includes('<strong>')) text = `<strong>${text}</strong>`;
                    if (node.italic && !text.includes('<em>')) text = `<em>${text}</em>`;
                    return text;
                }

                if (node.type === 'link') {
                    const innerHtml = Array.isArray(node.children) ? node.children.map(parseInlineNode).join('') : '';
                    if (innerHtml.trim().startsWith('<a')) return innerHtml;

                    const url = node.url || '#';
                    const target = node.target ? ` target="${node.target}"` : '';
                    return `<a href="${url}"${target}>${innerHtml}</a>`;
                }

                if (Array.isArray(node.children)) {
                    return node.children.map(parseInlineNode).join('');
                }

                return '';
            };

            const parseBlockNode = (node) => {
                if (!node) return '';
                if (!node.children) return '';

                if (node.type === 'list') {
                    const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
                    const innerContent = node.children.map(parseBlockNode).join('');
                    return `<${listTag}>${innerContent}</${listTag}>`;
                }

                if (node.type === 'list-item') {
                    const innerContent = node.children.map((child) => {
                        return child.type === 'list' ? parseBlockNode(child) : parseInlineNode(child);
                    }).join('');
                    return `<li>${innerContent}</li>`;
                }

                return node.children.map(parseInlineNode).join('');
            };

            for (let i = 0; i < children.length; i++) {
                const node = children[i];
                if (!node.children) continue;

                if (node.type !== 'list' && node.type !== 'list-item') {
                    const rawText = node.children.map(c => c.value || '').join('');
                    if (rawText.trim() === '' || rawText === '</br>' || rawText === '<br>') {
                        html.push('<br>');
                        continue;
                    }
                }

                if (node.type === 'heading') {
                    const level = node.level || 1;
                    const text = node.children.map(parseInlineNode).join('');
                    html.push(`<h${level}>${text}</h${level}>`);
                } else if (node.type === 'list' || node.type === 'list-item') {
                    html.push(parseBlockNode(node));
                } else {
                    let text = node.children.map(parseInlineNode).join('');

                    if (/^\s*<(section|div|style|script|button|header|footer|noscript)\b/i.test(text.trim())) {
                        html.push(text);
                    } else {
                        html.push('<p>' + text + '</p>');
                    }
                }
            }
            return html.join('');
        } catch (e) {
            return '';
        }
    }
}

// Create global instance
const storeLocator = new StoreLocator();

// Expose initMap as global function for Google Maps callback
getWindow().initMap = function () {
    storeLocator.initMap();
};

// If Maps loaded before this script (e.g. from head) and already called the no-op callback,
(function () {
    const win = getWindow();
    if (typeof win.google !== 'undefined' && win.google?.maps) {
        const listMapEl = document.querySelector(storeLocator.selectors.map.listMap);
        if (listMapEl) {
            storeLocator.initMap();
        }
    }
})();

// Handle Google Maps API loading errors
window.addEventListener('error', function (e) {
    if (e.filename && e.filename.includes('maps.googleapis.com')) {
        console.error('Google Maps API failed to load:', e);
        storeLocator.handleGoogleMapsUnavailable();
    }
}, true);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", () => {
        storeLocator.init();
    });
} else {
    storeLocator.init();
}

// ==================== TEST HELPER FUNCTIONS ====================
// Expose storeLocator globally for testing
getWindow().storeLocator = storeLocator;

/**
 * Test helper: Simulate Google Maps error
 * Usage in console: testGoogleMapsError()
 */
getWindow().testGoogleMapsError = function () {
    // Simulate Google Maps unavailable
    storeLocator.isGoogleMapsAvailable = false;
    storeLocator.handleGoogleMapsUnavailable();

    return {
        success: true,
        message: 'Fallback test completed. Check the page.',
        storesCount: getWindow().STORE_LOCATIONS?.length || 0
    };
};

/**
 * Test helper: Restore Google Maps (if available)
 * Usage in console: restoreGoogleMaps()
 */
getWindow().restoreGoogleMaps = function () {
    const win = getWindow();
    if (typeof win.google !== 'undefined' && win.google?.maps) {
        storeLocator.isGoogleMapsAvailable = true;
        storeLocator.initMap();
        return { success: true, message: 'Google Maps restored' };
    } else {
        console.warn('⚠️ Google Maps API not available. Cannot restore.');
        return { success: false, message: 'Google Maps API not loaded' };
    }
};
